import { useRef, useMemo, useState, useEffect } from 'react';
import { Box, Grid, type Theme, useMediaQuery } from '@mui/material';
import { SearchResults } from '@/components/feature/searchDirectory/SearchResults';
import { useSearchDirectoryData } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as CaregiverSearchResult } from '@/__generated__/seoSegmentationListingPage';
import {
  type FiltersContext,
  SegmentationFiltersContext,
} from '@/components/contexts/SegmentationFiltersContext';
import LocationFilter from '@/components/Filters/LocationFilter';
import { FiltersPopover } from '@/components/Filters/FiltersPopover';
import { FiltersModal } from '@/components/Filters/FiltersModal';
import OffsetPagination from '@/components/SearchResults/Pagination';
import { useSearchResults } from '@/components/feature/searchDirectory/useSearchResults';
import {
  SEOSegmentationFilterInput,
  SEOSegmentationPageType,
  SEOSegmentationSearchingFor,
} from '@/__generated__/globalTypes';
import { SERVICE_ID_FOR_MEMBER_TO_KEY } from '@/constants';
import { useLDClientFlags } from '@/components/hooks/useLDClientFlags';
import { useSearchAdConfig } from '@/components/feature/searchDirectory/hooks/useSearchAdConfig';
import { ProductSchema } from '@/components/feature/searchDirectory/ProductSchema';
import { ItemListSchema } from '@/components/feature/searchDirectory/ItemListSchema';
import { Illustration } from '@/components/feature/searchDirectory/Illustration';
import { NoSearchResults } from '@/components/feature/searchDirectory/NoSearchResults';
import { CaregiverCard } from './CaregiverCard/CaregiverCard';
import { shouldShowUserFilters } from '../helpers/shouldShowUserFilters';

export interface SearchResultsContainerProps {
  Aside: React.ComponentType;
}

export const SearchResultsContainer = ({ Aside }: SearchResultsContainerProps) => {
  const { seoSegmentationSearchResults, seoSegmentationDetails } = useSearchDirectoryData();
  const { seoSegmentId, searchingFor } = usePageAttributes();
  const {
    caregiverSearchResults: initialCaregiverSearchResults,
    filters: initialFilters,
    pagination: initialPagination,
  } = seoSegmentationSearchResults;

  const isNationalPage = seoSegmentationDetails.pageType === SEOSegmentationPageType.NATIONAL;
  const showUserFilters = shouldShowUserFilters(
    searchingFor ? SEOSegmentationSearchingFor[searchingFor] : undefined,
    SERVICE_ID_FOR_MEMBER_TO_KEY[seoSegmentationDetails.serviceIdForMember]
  );

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const searchFiltersRef = useRef<HTMLDivElement>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isScrollingDown = window.scrollY > lastScrollY;
        lastScrollY = window.scrollY;

        if (entry.isIntersecting) {
          setIsVisible(true);
        } else if (!entry.isIntersecting && isScrollingDown) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      {
        threshold: 0.1,
      }
    );

    const currentSearchResult = searchResultRef.current;

    if (currentSearchResult) {
      observer.observe(currentSearchResult);
    }

    return () => {
      if (currentSearchResult) {
        observer.unobserve(currentSearchResult);
        observer.disconnect();
      }
    };
  }, []);

  const isExtraSmallViewport = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')); // Mobile Phones
  const isSmallViewport = useMediaQuery((theme: Theme) => theme.breakpoints.only('sm')); // Small Tablets

  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const showGAMAds = GAMFlag?.variationIndex === 1;
  const adConfig = useSearchAdConfig(showGAMAds);

  const asideLocation = () => {
    if (isSmallViewport || isExtraSmallViewport) return 'TOP';
    return 'BOTTOM';
  };

  const {
    filters,
    setFilters,
    pagination,
    caregiverSearchResults,
    pageNumber,
    handlePageClick: baseHandlePageClick,
    fetchResults: baseFetchResults,
  } = useSearchResults({
    initialFilters,
    initialPagination,
    seoSegmentId,
    isNationalPage,
    showUserFilters,
    initialSearchResults: {
      caregiverSearchResults: initialCaregiverSearchResults,
    },
  });

  const handlePageClick = (newPage: number) => {
    baseHandlePageClick(newPage, searchResultRef);
  };

  const filtersCtx: FiltersContext = useMemo(() => {
    return {
      filters,
      setFilters: (filterVals: any) => {
        setFilters(filterVals);
      },
      fetchResults: (filterVals: SEOSegmentationFilterInput, resetPage: boolean) => {
        baseFetchResults(filterVals, resetPage, searchResultRef);
      },
    };
  }, [filters, setFilters, baseFetchResults]);

  return (
    <SegmentationFiltersContext.Provider value={filtersCtx}>
      <Grid item xs={12}>
        {asideLocation() === 'TOP' && <Aside />}

        {!isExtraSmallViewport && !isSmallViewport && (
          <Box mt={4} ref={searchFiltersRef} sx={{ display: 'flex', '& .MuiBox-root': { mb: 0 } }}>
            {isNationalPage && <LocationFilter />}
            {showUserFilters && <FiltersPopover />}
          </Box>
        )}
      </Grid>
      <Grid container mt={4}>
        <Grid item xs={12} md={8}>
          {caregiverSearchResults.length === 0 ? (
            <NoSearchResults />
          ) : (
            <>
              <Box ref={searchResultRef} />
              <Box position="relative">
                {caregiverSearchResults.length >= 3 && (
                  <Illustration
                    sx={{
                      position: 'absolute',
                      right: { md: '-325px', xl: '-475px' },
                      top: { md: '650px', xl: '500px' },
                    }}
                  />
                )}
                <SearchResults<CaregiverSearchResult>
                  items={caregiverSearchResults}
                  renderItem={(item, i) => (
                    <CaregiverCard searchResult={item} key={`product-${i}`} />
                  )}
                  ads={adConfig}
                />

                <ItemListSchema
                  searchResultType="CAREGIVER"
                  caregiverSearchResults={caregiverSearchResults}
                  totalNumberOfResults={pagination.totalNumberOfResults}
                  startIndex={pagination.startIndex}
                />

                <ProductSchema />

                <OffsetPagination
                  onPageClick={handlePageClick}
                  pagination={pagination}
                  segmentId={seoSegmentId}
                  pageSelected={pageNumber}
                />
              </Box>
            </>
          )}
        </Grid>
        {/* 
              Offset md 1 grid column 
              Grid v2 adds offset prop, but we are still on grid v1
              https://v5.mui.com/material-ui/react-grid2/#offset
            */}
        <Grid item xs={false} md={1} />
        {asideLocation() === 'BOTTOM' && (
          <Grid item xs={12} md={3}>
            <Aside />
          </Grid>
        )}
      </Grid>
      {(isExtraSmallViewport || isSmallViewport) && showUserFilters && (
        <FiltersModal isVisible={isVisible} />
      )}
    </SegmentationFiltersContext.Provider>
  );
};
